
import React from 'react';
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CalendarDays, MapPin, Users, Zap, Plane, Calendar, Share2, Mail, Crown } from 'lucide-react';
import { EventWithStatus, EVENT_TYPES } from '@/types/event';
import { linkifyText } from '@/utils/linkify';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface EventCardProps {
  event: EventWithStatus;
  onRegister?: (event_id: string) => void;
  isRegistered?: boolean;
  onShareToNetwork?: (event_id: string) => void;
}

const EventCard: React.FC<EventCardProps> = ({ event, onRegister, isRegistered = false, onShareToNetwork }) => {
  const { user, subscription } = useAuth();
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'bg-blue-100 text-blue-800';
      case 'ongoing':
        return 'bg-green-100 text-green-800';
      case 'past':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'À venir';
      case 'ongoing':
        return 'En cours';
      case 'past':
        return 'Passé';
      default:
        return status;
    }
  };

  const handleRegister = () => {
    if (onRegister) {
      onRegister(event.id);
    }
  };

  const handleInviteMembers = async () => {
    if (!user) {
      toast.error("Connexion requise");
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('send-event-invitations', {
        body: { event_id: event.id }
      });

      if (error) throw error;

      toast.success("Invitations envoyées", {
        description: `${data.totalSent} invitations ont été envoyées avec succès.`
      });
    } catch (error) {
      console.error('Error sending invitations:', error);
      toast.error("Erreur lors de l'envoi des invitations");
    }
  };

  const isAdmin = async () => {
    if (!user) return false;
    try {
      const { data } = await supabase.rpc('get_user_role', { user_id: user.id });
      return data === 'admin';
    } catch {
      return false;
    }
  };

  const eventType = EVENT_TYPES[event.event_type] || EVENT_TYPES.standard;
  const EventTypeIcon = eventType.icon === 'Zap' ? Zap : eventType.icon === 'Plane' ? Plane : eventType.icon === 'MapPin' ? MapPin : Calendar;

  return (
    <Card className="overflow-hidden h-full flex flex-col">
      {event.image_url ? (
        <div className="h-48 overflow-hidden relative">
          <img 
            src={event.image_url} 
            alt={event.title} 
            className="w-full h-full object-cover transition-transform hover:scale-105"
          />
          {/* Badge type d'événement en overlay */}
          <div 
            className="absolute top-3 left-3 px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1"
            style={{ 
              backgroundColor: eventType.bgColor,
              color: eventType.color,
              border: `1px solid ${eventType.color}`
            }}
          >
            <EventTypeIcon className="h-3 w-3" />
            {eventType.label}
          </div>
        </div>
      ) : (
        <div 
          className="h-48 flex items-center justify-center relative"
          style={{ backgroundColor: eventType.bgColor }}
        >
          <EventTypeIcon className="h-12 w-12" style={{ color: eventType.color }} />
          {/* Badge type d'événement */}
          <div 
            className="absolute top-3 left-3 px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1"
            style={{ 
              backgroundColor: eventType.color,
              color: 'white'
            }}
          >
            <EventTypeIcon className="h-3 w-3" />
            {eventType.label}
          </div>
        </div>
      )}
      
      <CardHeader className="p-4 pb-0">
        <div className="flex justify-between items-start mb-2">
          <div className="flex gap-2 flex-wrap">
            <Badge className={getStatusColor(event.status)}>
              {getStatusText(event.status)}
            </Badge>
            
            {event.is_premium && (
              <Badge className="bg-node-copper text-white">
                Premium
              </Badge>
            )}
            
            {event.event_type === 'vip' && (
              <Badge className="bg-gradient-to-r from-amber-500 to-amber-600 text-white border-0">
                <Crown className="h-3 w-3 mr-1" />
                VIP
              </Badge>
            )}
          </div>
        </div>
        <h3 className="text-lg font-bold">{event.title}</h3>
        {/* Affichage du type et description pour des types spéciaux */}
        {(event.event_type !== 'standard') && (
          <p className="text-xs text-muted-foreground mt-1">
            {eventType.description}
          </p>
        )}
      </CardHeader>
      
      <CardContent className="p-4 pt-2 flex-grow">
        {event.description && (
          <p className="text-gray-600 text-sm mb-4 line-clamp-3">
            {linkifyText(event.description)}
          </p>
        )}
        
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <CalendarDays className="h-4 w-4 text-node-copper" />
            <span>{formatDate(event.start_date)}</span>
          </div>
          
          {event.location && (
            <div className="flex items-center gap-2 text-sm text-gray-600 min-w-0">
              <MapPin className="h-4 w-4 text-node-copper flex-shrink-0" />
              <span className="truncate">{linkifyText(event.location)}</span>
            </div>
          )}
          
          {event.attendees && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Users className="h-4 w-4 text-node-copper" />
              <span>{event.attendees} participants</span>
            </div>
          )}
          
          {/* Informations spécifiques selon le type */}
          {event.duration_days && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <CalendarDays className="h-4 w-4 text-node-copper" />
              <span>{event.duration_days} jours</span>
            </div>
          )}
          
          {event.destination_country && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <MapPin className="h-4 w-4 text-node-copper" />
              <span>Destination: {event.destination_country}</span>
            </div>
          )}
        </div>
      </CardContent>
      
      <CardFooter className="p-4 pt-0 flex flex-col gap-2">
        {onShareToNetwork && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onShareToNetwork(event.id);
            }}
            className="w-full text-muted-foreground hover:text-blue-600"
            title="Partager dans le réseau"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Partager dans le réseau
          </Button>
        )}
        
        <div className="flex gap-2">
          {(() => {
            const isVipEvent = event.event_type === 'vip';
            const hasVipAccess = subscription === 'Enterprise';
            const canAccessVip = !isVipEvent || hasVipAccess;
            const isPastEvent = event.status === 'past';
            
            return (
              <Button 
                variant={isRegistered ? "secondary" : "primary"}
                onClick={handleRegister}
                disabled={isPastEvent || !canAccessVip}
                className="flex-1"
              >
                {isRegistered 
                  ? "Déjà inscrit" 
                  : !canAccessVip 
                    ? (
                      <>
                        <Crown className="h-4 w-4 mr-2" />
                        Réservé Enterprise
                      </>
                    )
                    : isPastEvent 
                      ? "Événement terminé" 
                      : "S'inscrire"
                }
              </Button>
            );
          })()}
          
          {user && event.status === 'upcoming' && (
            <Button 
              variant="primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleInviteMembers();
              }}
              className="px-3"
              title="Inviter des membres à cet événement"
            >
              <Mail className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardFooter>
    </Card>
  );
};

export default EventCard;
